import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaBell, FaToggleOn, FaToggleOff, FaHistory, FaSearch, FaTimes, FaUsers, FaUser } from 'react-icons/fa';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const ICON_OPTIONS = [
  'cafe', 'leaf', 'ice-cream', 'restaurant', 'fast-food', 'nutrition',
  'pricetag', 'star', 'gift', 'diamond', 'pizza', 'beer', 'wine-outline',
  'heart', 'ribbon', 'trophy', 'flame',
];

const COLOR_OPTIONS = [
  '#7C4DFF', '#43A047', '#E91E8C', '#FF8F00', '#FF6D00',
  '#8D6E63', '#1E88E5', '#E63950', '#10B981', '#F59E0B', '#06B6D4', '#6D28D9',
];

const GRADIENT_PRESETS = [
  { colors: ['#7C4DFF', '#512DA8'], label: 'Bənövşəyi' },
  { colors: ['#43A047', '#2E7D32'], label: 'Yaşıl' },
  { colors: ['#E91E8C', '#AD1457'], label: 'Çəhrayı' },
  { colors: ['#FF8F00', '#E65100'], label: 'Narıncı' },
  { colors: ['#1E88E5', '#1565C0'], label: 'Mavi' },
  { colors: ['#E63950', '#C4253C'], label: 'Qırmızı' },
  { colors: ['#10B981', '#059669'], label: 'Nəfti' },
  { colors: ['#F59E0B', '#D97706'], label: 'Sarı' },
  { colors: ['#6D28D9', '#E63950'], label: 'Gradient' },
];

const EMPTY_FORM = {
  title: '',
  description: '',
  icon_name: 'pricetag',
  icon_color: '#E63950',
  gradient_colors: ['#E63950', '#C4253C'],
  points_cost: 500,
  category: 'food',
  sort_order: 0,
  is_active: true,
};

const CARD = {
  background: '#fff', borderRadius: 16, padding: '20px 24px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 16,
};

const CAMPAIGN_TEMPLATES = [
  { icon: '🎁', label: 'Yeni kupon',       title: '🎁 Yeni kupon əlavə edildi!',        body: 'Xallarınızı kuponlara çevirin! Wallet bölməsindən yeni kuponlara baxın.' },
  { icon: '🔥', label: 'Xüsusi endirim',   title: '🔥 Xüsusi endirim kuponu!',           body: 'Məhdud müddətli kuponlar Cafegoo-da sizi gözləyir. İndi al, qaçırma!' },
  { icon: '⭐', label: 'Xal kampaniyası',  title: '⭐ Xal kampaniyası başladı!',          body: 'Xallarınızı pulsuz yeməklərə və endirim kuponlarına çevirin. İndi baxın!' },
  { icon: '🎯', label: 'Xatırlatma',       title: 'Kuponlarınızı unutmayın!',             body: 'Yetərli xalınız var — Wallet bölməsinə baxın və kupon əldə edin 🎟️' },
];

export default function CouponsPage() {
  const { getAdminToken } = useAuth();
  const [tab, setTab]           = useState('catalog');
  const [catalog, setCatalog]   = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);

  // Campaign
  const [campTitle, setCampTitle]     = useState('');
  const [campBody, setCampBody]       = useState('');
  const [sending, setSending]         = useState(false);
  const [sendResult, setSendResult]   = useState(null);
  const [targetMode, setTargetMode]   = useState('all'); // 'all' | 'selected'
  const [userSearch, setUserSearch]   = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching]     = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const searchTimer = useRef(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const [catRes, campRes] = await Promise.all([
      supabase.from('coupon_catalog').select('*').order('sort_order').order('created_at'),
      supabase.from('coupon_campaigns').select('*').order('created_at', { ascending: false }).limit(30),
    ]);
    setCatalog(catRes.data || []);
    setCampaigns(campRes.data || []);
    setLoading(false);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.points_cost) return;
    setSaving(true);
    if (editingId) {
      await supabase.from('coupon_catalog').update(form).eq('id', editingId);
    } else {
      await supabase.from('coupon_catalog').insert(form);
    }
    await loadAll();
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSaving(false);
  };

  const handleEdit = (item) => {
    setForm({
      title:           item.title,
      description:     item.description || '',
      icon_name:       item.icon_name || 'pricetag',
      icon_color:      item.icon_color || '#E63950',
      gradient_colors: item.gradient_colors || ['#E63950', '#C4253C'],
      points_cost:     item.points_cost,
      category:        item.category || 'food',
      sort_order:      item.sort_order || 0,
      is_active:       item.is_active !== false,
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kuponu silmək istəyirsiniz?')) return;
    await supabase.from('coupon_catalog').delete().eq('id', id);
    setCatalog(prev => prev.filter(c => c.id !== id));
  };

  const handleToggle = async (item) => {
    const next = !item.is_active;
    await supabase.from('coupon_catalog').update({ is_active: next }).eq('id', item.id);
    setCatalog(prev => prev.map(c => c.id === item.id ? { ...c, is_active: next } : c));
  };

  const handleUserSearch = (val) => {
    setUserSearch(val);
    clearTimeout(searchTimer.current);
    if (!val.trim() || val.trim().length < 2) { setUserResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await supabase.rpc('admin_search_users', {
          p_admin_token: getAdminToken(), p_query: val.trim(), p_exclude_business: true, p_limit: 20,
        });
        setUserResults(data?.success ? data.rows : []);
      } catch { setUserResults([]); }
      finally { setSearching(false); }
    }, 300);
  };

  const toggleUser = (u) => {
    setSelectedUsers(prev =>
      prev.find(x => x.id === u.id)
        ? prev.filter(x => x.id !== u.id)
        : [...prev, { id: u.id, full_name: u.full_name, email: u.email }]
    );
  };

  const handleSendCampaign = async () => {
    if (!campTitle.trim() || !campBody.trim()) return;
    if (targetMode === 'selected' && selectedUsers.length === 0) return;
    setSending(true);
    setSendResult(null);
    try {
      const userIds = targetMode === 'selected' ? selectedUsers.map(u => u.id) : null;

      const { data, error } = await supabase.rpc('broadcast_push', {
        p_title:    campTitle,
        p_body:     campBody,
        p_channel:  'campaigns',
        p_user_ids: userIds,
      });
      if (error) throw new Error(error.message);
      const total = data?.total ?? 0;

      await supabase.rpc('insert_campaign_notifications', {
        p_title:    campTitle,
        p_body:     campBody,
        p_user_ids: userIds,
      });

      await supabase.from('coupon_campaigns').insert({
        title: campTitle, message: campBody, sent_count: total,
      });
      setSendResult({ total, targeted: targetMode === 'selected' ? selectedUsers.length : null });
      await loadAll();
      setCampTitle('');
      setCampBody('');
      setSelectedUsers([]);
      setUserSearch('');
      setUserResults([]);
    } catch (err) {
      setSendResult({ error: err.message });
    } finally {
      setSending(false);
    }
  };

  const setGradient = (preset) => {
    setForm(f => ({ ...f, gradient_colors: preset.colors, icon_color: preset.colors[0] }));
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts);
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (m < 1) return 'İndicə';
    if (m < 60) return `${m} dəq əvvəl`;
    if (h < 24) return `${h} saat əvvəl`;
    return `${d} gün əvvəl`;
  };

  return (
    <div style={{ padding: 24, fontFamily: 'sans-serif', maxWidth: 960, margin: '0 auto' }}>

      {/* ── HEADER ── */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>
          🎟️ Kupon İdarəetməsi
        </h1>
        <p style={{ color: '#64748B', fontSize: 14, marginTop: 6 }}>
          Kupon kataloqonu idarə et, kampaniyalar yarat və istifadəçilərə bildiriş göndər
        </p>
      </div>

      {/* ── TABS ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[
          { key: 'catalog',   label: '📋 Kupon Kataloqu' },
          { key: 'campaigns', label: '📣 Kampaniyalar' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: tab === t.key ? '#E63950' : '#F1F5F9',
              color: tab === t.key ? '#fff' : '#64748B',
              fontWeight: 700, fontSize: 14,
            }}
          >{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8', fontSize: 16 }}>Yüklənir...</div>
      ) : tab === 'catalog' ? (

        /* ══════════════════ CATALOG TAB ══════════════════ */
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>
              {catalog.length} kupon ({catalog.filter(c => c.is_active).length} aktiv)
            </span>
            <button
              onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 10, border: 'none',
                background: '#E63950', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}
            >
              <FaPlus size={13} /> Yeni Kupon
            </button>
          </div>

          {/* Catalog list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {catalog.map(item => (
              <div key={item.id} style={{
                ...CARD, padding: '14px 18px',
                display: 'flex', alignItems: 'center', gap: 14,
                opacity: item.is_active ? 1 : 0.55,
                borderLeft: `4px solid ${item.is_active ? (item.icon_color || '#E63950') : '#E2E8F0'}`,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: (item.icon_color || '#E63950') + '20',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22,
                }}>
                  🎟️
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>{item.title}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                      background: item.category === 'discount' ? '#EFF6FF' : '#F0FDF4',
                      color:      item.category === 'discount' ? '#1D4ED8' : '#16A34A',
                    }}>{item.category}</span>
                    {!item.is_active && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#FEF2F2', color: '#DC2626' }}>
                        Deaktiv
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>{item.description}</div>
                </div>
                <div style={{
                  fontWeight: 800, fontSize: 15, color: item.icon_color || '#E63950',
                  background: (item.icon_color || '#E63950') + '15',
                  padding: '6px 12px', borderRadius: 10, flexShrink: 0,
                }}>
                  💎 {item.points_cost?.toLocaleString()} xal
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => handleToggle(item)}
                    title={item.is_active ? 'Deaktiv et' : 'Aktiv et'}
                    style={{
                      width: 34, height: 34, borderRadius: 8, border: 'none',
                      background: item.is_active ? '#ECFDF5' : '#F1F5F9',
                      color: item.is_active ? '#10B981' : '#94A3B8',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {item.is_active ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    style={{
                      width: 34, height: 34, borderRadius: 8, border: 'none',
                      background: '#EFF6FF', color: '#3B82F6', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <FaEdit size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    style={{
                      width: 34, height: 34, borderRadius: 8, border: 'none',
                      background: '#FEF2F2', color: '#EF4444', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <FaTrash size={13} />
                  </button>
                </div>
              </div>
            ))}

            {catalog.length === 0 && (
              <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8', fontSize: 15 }}>
                Hələ kupon yoxdur. "Yeni Kupon" düyməsindən əlavə edin.
              </div>
            )}
          </div>
        </div>

      ) : (

        /* ══════════════════ CAMPAIGNS TAB ══════════════════ */
        <div>
          {/* Create campaign */}
          <div style={CARD}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>
              📣 Yeni Kupon Kampaniyası
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748B' }}>
              Bütün istifadəçilərə push bildiriş göndər — kupon kataloqunu kəşf etsinlər.
            </p>

            {/* Target mode */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {[
                { key: 'all',      icon: <FaUsers size={13} />,  label: 'Bütün İstifadəçilər' },
                { key: 'selected', icon: <FaUser  size={13} />,  label: 'Seçilmişlərə'        },
              ].map(m => (
                <button
                  key={m.key}
                  onClick={() => { setTargetMode(m.key); setSelectedUsers([]); setUserSearch(''); setUserResults([]); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                    background: targetMode === m.key ? '#1a1a2e' : '#F1F5F9',
                    color:      targetMode === m.key ? '#fff'     : '#64748B',
                    fontWeight: 600, fontSize: 13,
                  }}
                >{m.icon} {m.label}</button>
              ))}
            </div>

            {/* User search (selected mode) */}
            {targetMode === 'selected' && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <FaSearch size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                  <input
                    value={userSearch}
                    onChange={e => handleUserSearch(e.target.value)}
                    placeholder="Ad, email və ya telefon ilə axtar..."
                    style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 10, border: '1.5px solid #E2E8F0', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                  {searching && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#94A3B8' }}>Axtarılır...</span>}
                </div>

                {userResults.length > 0 && (
                  <div style={{ border: '1.5px solid #E2E8F0', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
                    {userResults.map(u => {
                      const isSelected = selectedUsers.find(x => x.id === u.id);
                      return (
                        <div
                          key={u.id}
                          onClick={() => toggleUser(u)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                            cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                            background: isSelected ? '#F0FDF4' : '#fff',
                          }}
                        >
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: isSelected ? '#10B981' : '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                            {isSelected ? '✓' : (u.full_name?.[0] || '?').toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a2e' }}>{u.full_name || '—'}</div>
                            <div style={{ fontSize: 12, color: '#94A3B8' }}>{u.email}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {selectedUsers.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {selectedUsers.map(u => (
                      <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#1a1a2e', color: '#fff', borderRadius: 20, padding: '4px 10px 4px 12px', fontSize: 13, fontWeight: 600 }}>
                        {u.full_name || u.email}
                        <FaTimes size={10} style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => toggleUser(u)} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Templates */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Şablonlar:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {CAMPAIGN_TEMPLATES.map(t => (
                  <button
                    key={t.label}
                    onClick={() => { setCampTitle(t.title); setCampBody(t.body); setSendResult(null); }}
                    style={{
                      padding: '7px 14px', borderRadius: 20, border: '1.5px solid #E2E8F0',
                      background: '#F8FAFC', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#334155',
                    }}
                  >{t.icon} {t.label}</button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Bildiriş Başlığı
                </label>
                <input
                  value={campTitle}
                  onChange={e => { setCampTitle(e.target.value); setSendResult(null); }}
                  placeholder="məs. 🎁 Yeni kuponlar əlavə edildi!"
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10,
                    border: '1.5px solid #E2E8F0', fontSize: 14, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
                  Bildiriş Mətni
                </label>
                <textarea
                  value={campBody}
                  onChange={e => { setCampBody(e.target.value); setSendResult(null); }}
                  placeholder="İstifadəçilərə göndəriləcək mesaj..."
                  rows={3}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 10,
                    border: '1.5px solid #E2E8F0', fontSize: 14, outline: 'none',
                    resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
                  }}
                />
              </div>

              {/* Preview */}
              {(campTitle || campBody) && (
                <div style={{
                  background: '#F8FAFC', borderRadius: 12, padding: '14px 16px',
                  border: '1.5px solid #E2E8F0',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Önizləmə
                  </div>
                  <div style={{
                    background: '#1a1a2e', borderRadius: 12, padding: '12px 14px', maxWidth: 320,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{campTitle || '—'}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{campBody || '—'}</div>
                  </div>
                </div>
              )}

              <button
                onClick={handleSendCampaign}
                disabled={sending || !campTitle.trim() || !campBody.trim()}
                style={{
                  padding: '12px 24px', borderRadius: 10, border: 'none',
                  background: sending || !campTitle.trim() || !campBody.trim() ? '#E2E8F0' : '#E63950',
                  color: sending || !campTitle.trim() || !campBody.trim() ? '#94A3B8' : '#fff',
                  fontWeight: 700, fontSize: 14,
                  cursor: sending || !campTitle.trim() || !campBody.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'flex-start',
                }}
              >
                <FaBell size={14} />
                {sending ? 'Göndərilir...' : '🚀 Bütün İstifadəçilərə Göndər'}
              </button>

              {sendResult && (
                <div style={{
                  padding: '12px 16px', borderRadius: 10,
                  background: sendResult.error ? '#FEF2F2' : '#ECFDF5',
                  color: sendResult.error ? '#DC2626' : '#16A34A',
                  fontWeight: 600, fontSize: 14,
                }}>
                  {sendResult.error
                    ? `❌ Xəta: ${sendResult.error}`
                    : sendResult.targeted
                      ? `✅ ${sendResult.targeted} seçilmiş istifadəçiyə göndərildi`
                      : `✅ Bütün istifadəçilərə göndərildi (${sendResult.total} push token)`}
                </div>
              )}
            </div>
          </div>

          {/* Campaign history */}
          <div style={CARD}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 800, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaHistory size={15} color="#64748B" /> Kampaniya Tarixçəsi
            </h3>
            {campaigns.length === 0 ? (
              <p style={{ color: '#94A3B8', fontSize: 14, margin: 0 }}>Hələ kampaniya göndərilməyib.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {campaigns.map(c => (
                  <div key={c.id} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 14,
                    padding: '12px 14px', background: '#F8FAFC', borderRadius: 12,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, background: '#FEF3C7', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
                    }}>📣</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{c.title}</div>
                      <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>{c.message}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>{timeAgo(c.created_at)}</div>
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: 700, color: '#10B981',
                      background: '#ECFDF5', padding: '4px 10px', borderRadius: 20, flexShrink: 0,
                    }}>
                      {c.sent_count ?? 0} nəfər
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════ FORM MODAL ══════════════════ */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 16,
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: 28,
            width: '100%', maxWidth: 520,
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>
              {editingId ? '✏️ Kuponu Düzəlt' : '➕ Yeni Kupon'}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Title */}
              <div>
                <label style={lbl}>Başlıq *</label>
                <input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="məs. Pulsuz Kofe"
                  style={inp}
                />
              </div>

              {/* Description */}
              <div>
                <label style={lbl}>Açıqlama</label>
                <input
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="məs. Latte, kapuçino və digər kofe növlərinə aiddir"
                  style={inp}
                />
              </div>

              {/* Points cost + Category */}
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Xal Qiyməti *</label>
                  <input
                    type="number"
                    value={form.points_cost}
                    onChange={e => setForm(f => ({ ...f, points_cost: parseInt(e.target.value) || 0 }))}
                    style={inp}
                    min={1}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Kateqoriya</label>
                  <select
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    style={inp}
                  >
                    <option value="food">Yemək</option>
                    <option value="beverage">İçki</option>
                    <option value="discount">Endirim</option>
                    <option value="general">Ümumi</option>
                  </select>
                </div>
              </div>

              {/* Sort order */}
              <div>
                <label style={lbl}>Sıra (aşağı = öncə görünür)</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                  style={inp}
                  min={0}
                />
              </div>

              {/* Icon */}
              <div>
                <label style={lbl}>İkon</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ICON_OPTIONS.map(ic => (
                    <button
                      key={ic}
                      onClick={() => setForm(f => ({ ...f, icon_name: ic }))}
                      style={{
                        padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12,
                        background: form.icon_name === ic ? '#E63950' : '#F1F5F9',
                        color: form.icon_name === ic ? '#fff' : '#374151', fontWeight: 600,
                      }}
                    >{ic}</button>
                  ))}
                </div>
              </div>

              {/* Color */}
              <div>
                <label style={lbl}>Rəng</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {COLOR_OPTIONS.map(clr => (
                    <button
                      key={clr}
                      onClick={() => setForm(f => ({ ...f, icon_color: clr }))}
                      style={{
                        width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: 'pointer',
                        background: clr,
                        boxShadow: form.icon_color === clr ? `0 0 0 3px #fff, 0 0 0 5px ${clr}` : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Gradient */}
              <div>
                <label style={lbl}>Gradient</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {GRADIENT_PRESETS.map(g => {
                    const active = JSON.stringify(form.gradient_colors) === JSON.stringify(g.colors);
                    return (
                      <button
                        key={g.label}
                        onClick={() => setGradient(g)}
                        style={{
                          padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                          background: `linear-gradient(135deg, ${g.colors[0]}, ${g.colors[1]})`,
                          color: '#fff', fontSize: 12, fontWeight: 700,
                          boxShadow: active ? '0 0 0 3px #1a1a2e' : 'none',
                        }}
                      >{g.label}</button>
                    );
                  })}
                </div>
              </div>

              {/* Active toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  style={{ width: 18, height: 18 }}
                />
                <label htmlFor="is_active" style={{ fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                  Aktiv (mobile app-də görünsün)
                </label>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.title.trim()}
                  style={{
                    flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                    background: saving || !form.title.trim() ? '#E2E8F0' : '#E63950',
                    color: saving || !form.title.trim() ? '#94A3B8' : '#fff',
                    fontWeight: 700, fontSize: 14, cursor: saving || !form.title.trim() ? 'not-allowed' : 'pointer',
                  }}
                >{saving ? 'Saxlanır...' : editingId ? 'Yadda Saxla' : 'Əlavə Et'}</button>
                <button
                  onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}
                  style={{
                    padding: '12px 20px', borderRadius: 10, border: 'none',
                    background: '#F1F5F9', color: '#64748B', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                  }}
                >Ləğv Et</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const lbl = { fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 };
const inp = {
  width: '100%', padding: '10px 12px', borderRadius: 10,
  border: '1.5px solid #E2E8F0', fontSize: 14, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
};
